import { useEffect, useMemo, useRef, useState } from "react";
import { ALL_PAKISTAN_CITIES } from "../lib/pakistanCities";
import { Input } from "./ui/input";
import { CaretDown, MagnifyingGlass, X } from "@phosphor-icons/react";

/**
 * Searchable Pakistan-city picker.
 * - Type-ahead across 200+ official cities/towns
 * - "+ Other (city not in list)" reveals a free-text input INSIDE the open panel
 * - Commits the typed value to `onChange` as the user types
 */
export default function CitySelect({ value, onChange, placeholder = "Select your city", testid = "city-select", className = "", inputClassName = "" }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [otherMode, setOtherMode] = useState(false);
  const [otherText, setOtherText] = useState("");
  const ref = useRef(null);

  // Detect: is current value a typed "other" city?
  const isOther = !!value && !ALL_PAKISTAN_CITIES.includes(value);

  // Pre-populate Other mode + text when loading an existing custom value
  useEffect(() => {
    if (isOther) {
      setOtherText(value);
      setOtherMode(true);
    }
  }, [isOther, value]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return ALL_PAKISTAN_CITIES;
    return ALL_PAKISTAN_CITIES.filter((c) => c.toLowerCase().includes(q));
  }, [search]);

  const pickCity = (c) => {
    setOtherMode(false);
    setOtherText("");
    onChange(c);
    setSearch("");
    setOpen(false);
  };

  const enterOtherMode = () => {
    setOtherMode(true);
    // don't close the panel — keep it open so the input stays mounted
  };

  const onOtherChange = (v) => {
    setOtherText(v);
    onChange(v); // commit as they type
  };

  const display = value
    ? (isOther ? `${value} (other)` : value)
    : "";

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        type="button"
        data-testid={testid}
        onClick={() => setOpen((o) => !o)}
        className={`w-full text-left px-3 py-2 border-2 border-neutral-300 hover:border-black focus:outline-none focus:border-black bg-white flex items-center justify-between text-sm ${inputClassName}`}
      >
        <span className={display ? "text-black" : "text-neutral-400"}>{display || placeholder}</span>
        <CaretDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border-2 border-black shadow-lg max-h-[420px] overflow-hidden flex flex-col" data-testid={`${testid}-panel`}>
          {/* Search (hidden while in Other mode) */}
          {!otherMode && (
            <div className="p-2 border-b border-neutral-200 flex items-center gap-2 bg-neutral-50">
              <MagnifyingGlass size={14} className="text-neutral-400" />
              <input
                autoFocus
                data-testid={`${testid}-search`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search cities..."
                className="flex-1 bg-transparent outline-none text-sm"
              />
              {search && (
                <button type="button" onClick={() => setSearch("")} className="text-neutral-400 hover:text-black"><X size={14} /></button>
              )}
            </div>
          )}
          {/* List */}
          {!otherMode && (
            <div className="overflow-y-auto flex-1">
              {filtered.length === 0 ? (
                <div className="px-3 py-4 text-sm text-neutral-500">No matches. Pick "Other" below to add your own.</div>
              ) : (
                filtered.map((c) => (
                  <button
                    type="button"
                    key={c}
                    data-testid={`${testid}-opt-${c.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                    onClick={() => pickCity(c)}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-mango/10 hover:text-black ${value === c ? "bg-mango/20 font-bold" : ""}`}
                  >
                    {c}
                  </button>
                ))
              )}
            </div>
          )}
          {/* Other block */}
          {!otherMode ? (
            <div className="border-t-2 border-black bg-neutral-50">
              <button
                type="button"
                onClick={enterOtherMode}
                className="w-full text-left px-3 py-2.5 text-sm font-bold hover:bg-mango/10"
                data-testid={`${testid}-other-trigger`}
              >
                + Other (city not in list)
              </button>
            </div>
          ) : (
            <div className="p-3 bg-neutral-50 border-t-2 border-black space-y-2" data-testid={`${testid}-other`}>
              <div className="flex items-center justify-between">
                <div className="overline">YOUR CITY</div>
                <button
                  type="button"
                  onClick={() => { setOtherMode(false); setOtherText(""); onChange(""); }}
                  className="text-xs text-neutral-500 hover:text-black underline"
                  data-testid={`${testid}-other-back`}
                >
                  ← back to list
                </button>
              </div>
              <Input
                autoFocus
                data-testid={`${testid}-other-input`}
                value={otherText}
                onChange={(e) => onOtherChange(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); setOpen(false); } }}
                placeholder="Type your city name (e.g. Kotri-Jamshoro)"
                className="rounded-none text-sm"
              />
              <div className="flex justify-end">
                <button
                  type="button"
                  disabled={!otherText.trim()}
                  onClick={() => setOpen(false)}
                  className="px-3 py-1.5 text-xs bg-black text-white hover:bg-neutral-800 disabled:opacity-50"
                  data-testid={`${testid}-other-done`}
                >
                  Done
                </button>
              </div>
              <div className="text-[10px] text-neutral-500">Don't see your city in our list? Just type it above.</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
